/**
 * Pure rules for the Person view's civic record (prototype `person-page`,
 * direction B): how a run's ballots read, how the run ended, whether it is
 * still current, and the order of the timeline.
 */
import type { PublicCandidacySection, PublicRunBallot, PublicSeatAssignmentSection } from "./api";
import { dateParts } from "./model";

export type Outcome = "won" | "lost" | "withdrawn";

export type TimelineEntry =
  | { kind: "campaign"; key: string; current: boolean; outcome: Outcome | null; dateParts: string[]; candidacy: PublicCandidacySection }
  | { kind: "office"; key: string; dateParts: string[]; office: PublicSeatAssignmentSection };

export function personName(p: { firstName: string; lastName: string; displayName: string | null }): string {
  return p.displayName?.trim() || `${p.firstName} ${p.lastName}`.trim();
}

const byDate = (ballots: PublicRunBallot[]) => [...ballots].sort((a, b) => a.date.localeCompare(b.date));

/** A run's ballots as the campaign card lists them: latest Election date on top. */
export const newestBallotsFirst = (ballots: PublicRunBallot[]) => byDate(ballots).reverse();

/** Race names that only make sense beside their Body ("District 13", "Ward 1"). */
const PART_OF_BODY = /^(district|ward|sub-?district|sub-?circuit|at[- ]large|board president)\b/i;

/**
 * The office a run is for, in words a voter recognizes: a district names its
 * Body ("Illinois Senate, District 13"); a named office ("Mayor") stands alone.
 */
export function officeTitle(c: Pick<PublicCandidacySection, "race" | "body">): string {
  const race = c.race?.name?.trim() || null;
  const body = c.body?.name?.trim() || null;
  if (race && body && PART_OF_BODY.test(race)) return `${body}, ${race}`;
  return race ?? body ?? "Campaign";
}

/**
 * The office a Person holds, titled by the same rule as a run. A district or
 * ward seat names it even when the role has a title of its own ("Board Member"),
 * so the office reads like the campaign for it; the role then subtitles it.
 */
export function heldOfficeTitle(o: PublicSeatAssignmentSection): string {
  const seat = o.seat.name?.trim() || null;
  const name = seat && !o.seat.isAtLarge && PART_OF_BODY.test(seat) ? seat : o.roleTitle ?? seat ?? "";
  return officeTitle({
    race: { slug: o.seat.slug, name },
    body: { slug: o.body.slug, name: o.body.name },
  });
}

/** Seat or Race names that place an office rather than name it ("District 1A", "Ward 33"). */
const PLACE = /^(district|ward|sub-?district|sub-?circuit|at[- ]large)\b/i;
const clean = (v: string | null | undefined) => v?.trim() || null;
const within = (outer: string, inner: string) => outer.toLocaleLowerCase("en-US").includes(inner.toLocaleLowerCase("en-US"));

/**
 * A card's heading: what the office is called, then where it sits.
 * "Board Member" over "District 1A – Chicago Board of Education". Without a
 * role title, a named office ("Mayor") still titles itself; a district falls
 * back to its Body ("Illinois Senate" over "District 13"). The subtitle drops
 * whatever the title or the place already says.
 */
export function officeHeading(o: { role: string | null; place: string | null; body: string | null }): { title: string; subtitle: string | null } {
  const role = clean(o.role);
  const place = clean(o.place);
  const body = clean(o.body);
  const title = role ?? (place && !PLACE.test(place) ? place : body ?? place ?? "Office");
  const parts: string[] = [];
  if (place && !within(title, place)) parts.push(place);
  if (body && !within(title, body) && !(place && within(place, body))) parts.push(body);
  return { title, subtitle: parts.length ? parts.join(" – ") : null };
}

export type StatusPill = { kind: "result"; status: "won" | "lost" | "advanced" | "withdrawn"; text: string } | { kind: "status"; text: string };

const RESULTS = ["won", "lost", "advanced", "withdrawn", "withdrew"];

/**
 * A current run's standing in the Election page's words and pills: each decided
 * Election date as a result tag ("Won primary"), and the latest date's own status
 * ("Announced", "Write-In") when it has not been decided. Simply being on the
 * ballot says nothing, so it has no pill.
 */
export function campaignStatusPills(ballots: PublicRunBallot[], statusLabel: (status: string | null, stage: string) => string | null): StatusPill[] {
  const sorted = byDate(ballots);
  const pills: StatusPill[] = [];
  sorted.forEach((b, i) => {
    const writeIn = b.status?.endsWith("_write_in") ?? false;
    const onBallot = b.status === "withdrawn_on_ballot";
    const raw = b.status === "withdrew" || onBallot ? "withdrawn"
      : writeIn ? b.status!.slice(0, -"_write_in".length) : b.status;
    if (raw && RESULTS.includes(raw)) {
      const status = (raw === "won" && b.stage === "primary" ? "advanced" : raw) as "won" | "lost" | "advanced" | "withdrawn";
      const next = sorted[i + 1];
      const text = status === "advanced"
        ? b.stage === "primary" ? "Won primary" : next?.stage === "runoff" ? "Advanced to runoff" : "Advanced"
        : status === "withdrawn" ? onBallot ? "Withdrawn (still on ballot)" : "Withdrew"
        : status === "lost" && b.stage === "primary" ? "Lost primary"
        : `${status === "won" ? "Won" : "Lost"} ${b.label.toLowerCase()}`;
      pills.push({ kind: "result", status, text: writeIn ? `${text} (write-in)` : text });
    } else if (i === sorted.length - 1) {
      const text = statusLabel(b.status, b.stage);
      if (text) pills.push({ kind: "status", text });
    }
  });
  return pills;
}

export type EndorsementStanding = "endorsed" | "revoked" | null;

const standing = (b: PublicRunBallot): EndorsementStanding =>
  b.endorsed ? "endorsed" : b.endorsementRevoked ? "revoked" : null;

/**
 * Where a run's endorsement shows. One card-level pill when every Election
 * date shares a standing; per-date pills only when the standing changed
 * between dates (endorsed for the general only, or later withdrawn).
 */
export function endorsementSummary(ballots: PublicRunBallot[]): { card: EndorsementStanding; perBallot: boolean } {
  const standings = new Set(ballots.map(standing));
  if (standings.size > 1) return { card: null, perBallot: true };
  return { card: standings.values().next().value ?? null, perBallot: false };
}

/** What happened on one ballot, in the words of the campaign timeline. */
export function ballotLabel(b: PublicRunBallot): string {
  if (b.status?.endsWith("_write_in")) {
    return `${ballotLabel({ ...b, status: b.status.slice(0, -"_write_in".length) })} (write-in)`;
  }
  switch (b.status) {
    case "won":
      return b.stage === "primary" ? "Won the primary" : b.stage === "runoff" ? "Elected in the runoff" : "Elected";
    case "lost":
      return b.stage === "primary" ? "Lost the primary" : b.stage === "runoff" ? "Lost the runoff" : "Lost the general election";
    case "advanced":
      return "Advanced";
    case "withdrawn":
    case "withdrew":
      return "Withdrew";
    case "withdrawn_on_ballot":
      return "Withdrawn (still on ballot)";
    case "removed":
      return "Removed from the ballot";
    case "challenged":
      return "Ballot access challenged";
    case "announced":
      return "Announced";
    case "write_in":
      return "Write-In";
    case "prospective":
      return "Considering a run";
    default:
      return "On the ballot";
  }
}

/**
 * How the run ended and on which ballot — the one that gets the lost-run
 * treatment. Winning a primary is a nomination, not an outcome.
 */
export function runOutcome(ballots: PublicRunBallot[]): { outcome: Outcome | null; ballot: string | null } {
  const last = byDate(ballots).at(-1);
  if (!last) return { outcome: null, ballot: null };
  const status = last.status?.endsWith("_write_in") ? last.status.slice(0, -"_write_in".length) : last.status;
  if (status === "lost" || status === "removed") return { outcome: "lost", ballot: last.slug };
  if (status === "withdrawn" || status === "withdrew" || status === "withdrawn_on_ballot") return { outcome: "withdrawn", ballot: last.slug };
  if (status === "won" && last.stage !== "primary") return { outcome: "won", ballot: last.slug };
  return { outcome: null, ballot: null };
}

/** A run is current while a ballot is still ahead and it has not ended early. */
export function isCurrentRun(ballots: PublicRunBallot[], today: string): boolean {
  const last = byDate(ballots).at(-1);
  if (!last || last.date < today) return false;
  return runOutcome(ballots).outcome !== "withdrawn" && runOutcome(ballots).outcome !== "lost";
}

function lastDate(c: PublicCandidacySection): string {
  return byDate(c.ballots).at(-1)?.date ?? "";
}

/** Current campaigns newest first, then offices held, then past campaigns newest first. */
export function timelineEntries(
  candidacies: PublicCandidacySection[],
  offices: PublicSeatAssignmentSection[],
  today: string
): TimelineEntry[] {
  const campaign = (c: PublicCandidacySection): TimelineEntry => {
    const date = lastDate(c);
    const parts = date ? dateParts(date) : null;
    return {
      kind: "campaign",
      key: `campaign-${c.candidacyId}`,
      current: isCurrentRun(c.ballots, today),
      outcome: runOutcome(c.ballots).outcome,
      dateParts: parts ? [parts.month, parts.year] : [],
      candidacy: c,
    };
  };
  const newestFirst = (a: PublicCandidacySection, b: PublicCandidacySection) => lastDate(b).localeCompare(lastDate(a));
  const all = [...candidacies].sort(newestFirst).map(campaign);
  const current = all.filter((e) => e.kind === "campaign" && e.current);
  const past = all.filter((e) => e.kind === "campaign" && !e.current);
  const held: TimelineEntry[] = offices.map((o) => ({
    kind: "office",
    key: `office-${o.assignmentId}`,
    // An open span, one line each, so the year never runs into "Now": 2023 / to / Now.
    dateParts: o.assignment.startDate ? [o.assignment.startDate.slice(0, 4), "to", "Now"] : ["Now"],
    office: o,
  }));
  return [...current, ...held, ...past];
}
